import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../api/redux/store';
import { Button, Input, Select, Spin } from 'antd';
import {
  AudioOutlined,
  BulbOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  CloseOutlined,
  CommentOutlined,
  CustomerServiceOutlined,
  EnvironmentOutlined,
  FileSearchOutlined,
  PaperClipOutlined,
  RadarChartOutlined,
  RightOutlined,
  SendOutlined,
  ThunderboltOutlined,
  WalletOutlined,
  TranslationOutlined,
} from '@ant-design/icons';

import { selectCurrentUser } from '../../api/redux/authSlice';
import { clearCart, addToCart, setOrderNotes, setPaymentMethod } from '../../api/redux/cartSlice';
import { fetchAddresses, setSelectedAddressId } from '../../api/redux/addressSlice';
import { addressPickToPaymentRoute, loadLatestCompletedOrderPendingReview } from '../../ai/supportAssistantData';
import { clearMerchantOrderId, clearPendingOrderPayload } from '../../utils/phonepePaymentCache';
import AiSparkleIcon from '../common/AiSparkleIcon';
import { useServiceAreaCity } from '../../hooks/useServiceAreaCity';
import { askSupportAssistant } from '../../api/supportChatApi';
import {
  QUICK_SUGGESTION_CHIPS,
  QUICK_SUGGESTION_LABELS,
  RECENT_SERVICE_CHIPS,
  SUGGESTION_MESSAGES,
  THINKING_STEPS,
  WELCOME_HUB_ITEMS,
} from '../../ai/socChatUiConfig';
import SupportChatRichContent from './SupportChatRichContent';
import {
  EMPTY_SESSION,
  type AssistantMessage,
  type AssistantSession,
  type ChatAction,
  type OrderPick,
} from '../../ai/supportAssistantTypes';
import { useSupportChat } from '../../context/SupportChatContext';
import './SupportChatWidget.css';

type SpeechRecognitionResultLike = {
  isFinal: boolean;
  0: { transcript: string };
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: SpeechRecognitionResultLike[];
};

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

type IndianLanguage = {
  code: string;
  label: string;
  placeholder: string;
  voiceHint: string;
};

const INDIAN_LANGUAGES: IndianLanguage[] = [
  { code: 'en-IN', label: 'English', placeholder: 'Type or speak: book cleaning, track my orders...', voiceHint: 'Speak in English' },
  { code: 'hi-IN', label: 'Hindi', placeholder: 'Speak or type: AC repair book karo...', voiceHint: 'Speak in Hindi' },
  { code: 'bn-IN', label: 'Bengali', placeholder: 'Speak or type: booking, order track...', voiceHint: 'Speak in Bengali' },
  { code: 'te-IN', label: 'Telugu', placeholder: 'Speak or type: service book...', voiceHint: 'Speak in Telugu' },
  { code: 'mr-IN', label: 'Marathi', placeholder: 'Speak or type: booking, order track...', voiceHint: 'Speak in Marathi' },
  { code: 'ta-IN', label: 'Tamil', placeholder: 'Speak or type: service book...', voiceHint: 'Speak in Tamil' },
  { code: 'ur-IN', label: 'Urdu', placeholder: 'Speak or type: booking, order track...', voiceHint: 'Speak in Urdu' },
  { code: 'gu-IN', label: 'Gujarati', placeholder: 'Speak or type: service book...', voiceHint: 'Speak in Gujarati' },
  { code: 'kn-IN', label: 'Kannada', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Kannada' },
  { code: 'ml-IN', label: 'Malayalam', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Malayalam' },
  { code: 'pa-IN', label: 'Punjabi', placeholder: 'Speak or type: service book...', voiceHint: 'Speak in Punjabi' },
  { code: 'or-IN', label: 'Odia', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Odia' },
  { code: 'as-IN', label: 'Assamese', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Assamese' },
  { code: 'ne-IN', label: 'Nepali', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Nepali' },
  { code: 'kok-IN', label: 'Konkani', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Konkani' },
  { code: 'sa-IN', label: 'Sanskrit', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Sanskrit' },
  { code: 'doi-IN', label: 'Dogri', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Dogri' },
  { code: 'mai-IN', label: 'Maithili', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Maithili' },
  { code: 'mni-IN', label: 'Manipuri', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Manipuri' },
  { code: 'brx-IN', label: 'Bodo', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Bodo' },
  { code: 'ks-IN', label: 'Kashmiri', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Kashmiri' },
  { code: 'sd-IN', label: 'Sindhi', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Sindhi' },
  { code: 'sat-IN', label: 'Santali', placeholder: 'Speak or type: booking...', voiceHint: 'Speak in Santali' },
];

function cleanChipLabel(label: string): string {
  return label.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').trim();
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function renderMarkdownLite(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  const win = window as typeof window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return win.SpeechRecognition ?? win.webkitSpeechRecognition ?? null;
}

const HIDDEN_PATHS = ['/login', '/onboarding', '/admin', '/payment/callback'];
const BOTTOM_NAV_PATHS = ['/home', '/explore', '/booking-history', '/favorites', '/settings', '/payment'];
const REVIEW_SNOOZE_KEY = 'soc_ai_review_snoozed_orders';
const REVIEW_SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;

function readReviewSnoozes(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(REVIEW_SNOOZE_KEY) || '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

function isReviewSnoozed(orderId: string): boolean {
  const value = readReviewSnoozes()[orderId];
  return typeof value === 'number' && Date.now() - value < REVIEW_SNOOZE_MS;
}

function snoozeReview(orderId: string): void {
  const next = { ...readReviewSnoozes(), [orderId]: Date.now() };
  localStorage.setItem(REVIEW_SNOOZE_KEY, JSON.stringify(next));
}

function getHubIcon(id: string): React.ReactNode {
  const icon = (() => {
    switch (id) {
      case 'book':
        return <CalendarOutlined />;
      case 'photo':
        return <PaperClipOutlined />;
      case 'speak':
        return <AudioOutlined />;
      case 'track':
        return <RadarChartOutlined />;
      case 'payment':
        return <WalletOutlined />;
      case 'invoice':
        return <FileSearchOutlined />;
      case 'offers':
        return <CommentOutlined />;
      case 'support':
        return <CustomerServiceOutlined />;
      default:
        return <EnvironmentOutlined />;
    }
  })();
  return <span className="soc-ai-pill-icon">{icon}</span>;
}

function AssistantAvatar({ size = 16, pulse = false }: { size?: number; pulse?: boolean }) {
  return (
    <div className={`soc-ai-msg-avatar ${pulse ? 'soc-ai-msg-avatar--pulse' : ''}`} aria-hidden>
      <AiSparkleIcon size={size} variant="gradient" />
    </div>
  );
}

const SupportChatWidget: React.FC = () => {
  const { open, closeChat, toggleChat, pendingMessage, clearPendingMessage } = useSupportChat();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useSelector(selectCurrentUser);
  const areaCity = useServiceAreaCity();
  const token = localStorage.getItem('access_token');
  const liftFabAboveFixedFooter = location.pathname.startsWith('/cold-storage')
    || location.pathname.startsWith('/learn');

  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [session, setSession] = useState<AssistantSession>(EMPTY_SESSION);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [languageCode, setLanguageCode] = useState(() => localStorage.getItem('soc_ai_language') || 'en-IN');
  const [listening, setListening] = useState(false);
  const [thinkingStep, setThinkingStep] = useState(0);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [pendingReviewOrder, setPendingReviewOrder] = useState<OrderPick | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sendMessageRef = useRef<((text: string, displayText?: string) => Promise<void>) | null>(null);
  const sessionRef = useRef(session);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  sessionRef.current = session;

  const selectedLanguage = useMemo(
    () => INDIAN_LANGUAGES.find((lang) => lang.code === languageCode) ?? INDIAN_LANGUAGES[0],
    [languageCode]
  );

  const speechSupported = typeof window !== 'undefined' && Boolean(getSpeechRecognitionCtor());
  const showFab = Boolean(token) && !HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));
  const showBottomNav = BOTTOM_NAV_PATHS.includes(location.pathname);

  const assistantContext = useMemo(
    () => ({
      userName: user?.name ?? user?.phoneNumber ?? undefined,
      userId: user?.id ?? localStorage.getItem('user_id') ?? undefined,
      userPhone: user?.phoneNumber ?? undefined,
      currentPath: location.pathname,
      areaCity,
      languageCode,
      languageName: selectedLanguage.label,
    }),
    [user, location.pathname, areaCity, languageCode, selectedLanguage.label]
  );

  useEffect(() => {
    localStorage.setItem('soc_ai_language', languageCode);
  }, [languageCode]);

  useEffect(() => {
    if (!open) {
      setSession(EMPTY_SESSION);
      setMessages([]);
      setDraft('');
      setVoiceError(null);
      setPendingReviewOrder(null);
    }
  }, [open]);

  useEffect(() => {
    const userId = assistantContext.userId?.trim();
    if (!open || !userId) return;
    let cancelled = false;
    loadLatestCompletedOrderPendingReview(userId)
      .then((order) => {
        if (!cancelled) setPendingReviewOrder(order && !isReviewSnoozed(order.id) ? order : null);
      })
      .catch(() => {
        if (!cancelled) setPendingReviewOrder(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, assistantContext.userId]);

  useEffect(() => {
    if (!thinking) {
      setThinkingStep(0);
      return;
    }
    const timer = window.setInterval(() => {
      setThinkingStep((s) => (s + 1) % THINKING_STEPS.length);
    }, 1400);
    return () => window.clearInterval(timer);
  }, [thinking]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open || !bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, thinking, open]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  const sendMessage = useCallback(
    async (text: string, displayText?: string) => {
      const trimmed = text.trim();
      if (!trimmed || thinking) return;

      const userMsg: AssistantMessage = { id: newId(), role: 'user', content: displayText?.trim() || trimmed, timestamp: Date.now() };
      const nextMessages = [...messages, userMsg];
      setMessages(nextMessages);
      setDraft('');
      setThinking(true);

      try {
        const result = await askSupportAssistant(trimmed, nextMessages, assistantContext, sessionRef.current);
        setSession(result.session);
        setMessages((prev) => [
          ...prev,
          {
            id: newId(),
            role: 'assistant',
            content: result.reply.content,
            actions: result.reply.actions,
            rich: result.reply.rich,
            suggestions: result.reply.suggestions,
            timestamp: Date.now(),
          },
        ]);
      } catch {
        const fallback = 'I hit a connection issue. Please try again in a moment.';
        setMessages((prev) => [...prev, { id: newId(), role: 'assistant', content: fallback, timestamp: Date.now() }]);
      } finally {
        setThinking(false);
      }
    },
    [thinking, messages, assistantContext]
  );

  sendMessageRef.current = sendMessage;

  useEffect(() => {
    if (!open || !pendingMessage?.trim() || thinking) return;
    const msg = pendingMessage.trim();
    clearPendingMessage();
    const timer = window.setTimeout(() => {
      void sendMessageRef.current?.(msg);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [open, pendingMessage, clearPendingMessage, thinking]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setListening(false);
  }, []);

  const startListening = useCallback(() => {
    const Recognition = getSpeechRecognitionCtor();
    if (!Recognition) {
      setVoiceError('Voice input is not supported in this browser. Try Chrome on Android or desktop.');
      return;
    }

    setVoiceError(null);
    recognitionRef.current?.abort();
    const recognition = new Recognition();
    recognition.lang = languageCode;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = (event) => {
      setListening(false);
      setVoiceError(event.error === 'not-allowed' ? 'Microphone permission is blocked.' : 'Could not hear clearly. Please try again.');
    };
    recognition.onresult = (event) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0]?.transcript ?? '';
        if (event.results[i].isFinal) finalText += transcript;
        else interimText += transcript;
      }
      const heard = (finalText || interimText).trim();
      if (heard) setDraft(heard);
      if (finalText.trim()) void sendMessageRef.current?.(finalText.trim());
    };
    recognitionRef.current = recognition;
    recognition.start();
  }, [languageCode]);

  const runAction = useCallback(
    (action: ChatAction) => {
      if (action.type === 'prompt') {
        const display = action.message.startsWith('pick:') ? action.label : undefined;
        void sendMessageRef.current?.(action.message, display);
        return;
      }
      if (action.type === 'mailto') {
        window.location.href = `mailto:${action.email}`;
        return;
      }
      if (action.type === 'link') {
        window.open(action.href, '_blank', 'noopener,noreferrer');
        return;
      }
      if (action.type === 'checkout') {
        clearMerchantOrderId();
        clearPendingOrderPayload();

        const userId = user?.id ?? localStorage.getItem('user_id');
        if (userId) void dispatch(fetchAddresses(userId));

        dispatch(clearCart());
        dispatch(addToCart({
          id: action.checkout.service.id,
          name: action.checkout.service.name,
          price: action.checkout.service.price,
          quantity: 1,
        }));
        if (action.checkout.orderNotes) dispatch(setOrderNotes(action.checkout.orderNotes));
        dispatch(setPaymentMethod(action.checkout.paymentMethod ?? 'online'));
        dispatch(setSelectedAddressId(action.checkout.addressId));

        const preferredDate = new Date(action.checkout.preferredDate);
        const routeAddress = addressPickToPaymentRoute(action.checkout.address);

        navigate('/payment', {
          state: {
            address: routeAddress,
            preferredDate: Number.isNaN(preferredDate.getTime()) ? new Date() : preferredDate,
            preferredTime: action.checkout.preferredTime,
          },
        });
        setSession(EMPTY_SESSION);
        closeChat();
        return;
      }
      if (action.type === 'navigate') {
        navigate(action.path);
        closeChat();
      }
    },
    [closeChat, dispatch, navigate, user?.id]
  );

  const runSuggestion = useCallback(
    (label: string) => {
      const mapped = SUGGESTION_MESSAGES[label];
      if (mapped) void sendMessage(mapped);
      else void sendMessage(label);
    },
    [sendMessage],
  );

  const welcomeFirstName = useMemo(() => {
    const raw = assistantContext.userName?.split(' ')[0] ?? '';
    return raw && !/^admin$/i.test(raw) ? raw : '';
  }, [assistantContext.userName]);

  if (!showFab) return null;

  const showStandaloneFab = showFab && !showBottomNav;
  const conversationStarted = messages.some((m) => m.role === 'user');
  const isWelcome = open && messages.length === 0 && !thinking;

  return (
    <>
      {showStandaloneFab && (
        <button
          type="button"
          className={`soc-ai-fab ${open ? 'soc-ai-fab--open' : ''} ${liftFabAboveFixedFooter ? 'soc-ai-fab--above-footer' : ''}`}
          onClick={toggleChat}
          aria-label={open ? 'Close SOC AI' : 'Open SOC AI'}
        >
          {open ? <CloseOutlined /> : <AiSparkleIcon size={28} />}
        </button>
      )}

      {open && (
        <>
          <button
            type="button"
            className="soc-ai-backdrop"
            onClick={closeChat}
            aria-label="Close SOC AI"
          />
        <div className={`soc-ai-panel ${showBottomNav ? 'soc-ai-panel--with-nav' : ''}`} role="dialog" aria-label="SOC AI customer help">
          <header className="soc-ai-header">
            <div className="soc-ai-header-brand">
              <AiSparkleIcon size={20} variant="gradient" />
              <span className="soc-ai-title">SOC AI</span>
            </div>
            <Button type="text" icon={<CloseOutlined />} onClick={closeChat} aria-label="Close" className="soc-ai-close" />
          </header>

          {voiceError && <div className="soc-ai-voice-error" role="status">{voiceError}</div>}

          <div className="soc-ai-body" ref={bodyRef}>
            {isWelcome ? (
              <div className="soc-ai-welcome">
                <h1 className="soc-ai-greeting-hi">Hi{welcomeFirstName ? ` ${welcomeFirstName}` : ''},</h1>
                <p className="soc-ai-greeting-sub">How can I assist you today?</p>
                <div className="soc-ai-pill-row">
                  {WELCOME_HUB_ITEMS.map((item, idx) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`soc-ai-pill ${idx === 0 ? 'soc-ai-pill--accent' : ''}`}
                      onClick={() => {
                        if (item.id === 'speak') startListening();
                        else void sendMessage(item.message);
                      }}
                      disabled={thinking}
                    >
                      {getHubIcon(item.id)}
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="soc-ai-pill-row">
                  {QUICK_SUGGESTION_CHIPS.filter((k) => k !== 'Book Now').map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="soc-ai-pill"
                      onClick={() => runSuggestion(QUICK_SUGGESTION_LABELS[key])}
                      disabled={thinking}
                    >
                      {QUICK_SUGGESTION_LABELS[key]}
                    </button>
                  ))}
                </div>
                {pendingReviewOrder ? (
                  <div className="soc-ai-review-card">
                    <span className="soc-ai-review-kicker">Review pending</span>
                    <strong>How was your {pendingReviewOrder.service ?? 'last service'}?</strong>
                    <div className="soc-ai-review-stars" aria-label="Rate last completed service">
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => void sendMessage(`review:order:${pendingReviewOrder.id}:rating:${rating}`, `${rating} star${rating === 1 ? '' : 's'}`)}
                          disabled={thinking}
                        >
                          {rating}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="soc-ai-review-later"
                      onClick={() => {
                        snoozeReview(pendingReviewOrder.id);
                        setPendingReviewOrder(null);
                      }}
                    >
                      Not now
                    </button>
                  </div>
                ) : null}
                <p className="soc-ai-recent-label">Recent services</p>
                <div className="soc-ai-pill-row">
                  {RECENT_SERVICE_CHIPS.map((label) => (
                    <button
                      key={label}
                      type="button"
                      className="soc-ai-pill"
                      onClick={() => void sendMessage(`book ${label.toLowerCase()}`)}
                      disabled={thinking}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
            {messages.map((msg) => (
              <div key={msg.id} className={`soc-ai-msg-row soc-ai-msg-row--${msg.role}`}>
                {msg.role === 'assistant' ? <AssistantAvatar size={18} /> : null}
                <div className={`soc-ai-bubble soc-ai-bubble--${msg.role}`}>
                {msg.content ? <div className="soc-ai-bubble-text">{renderMarkdownLite(msg.content)}</div> : null}
                {msg.rich && msg.rich.length > 0 ? <SupportChatRichContent blocks={msg.rich} /> : null}
                {msg.actions && msg.actions.length > 0 && (
                  <div className={`soc-ai-actions ${msg.actions.some((a) => a.type === 'prompt' && a.style === 'card') ? 'soc-ai-actions--cards' : ''}`}>
                    {msg.actions.map((action, idx) => (
                      <button
                        key={`${msg.id}-action-${idx}`}
                        type="button"
                        className={`soc-ai-action-btn ${action.type === 'prompt' && action.style === 'card' ? 'soc-ai-action-btn--card' : ''} ${action.type === 'prompt' && action.style === 'primary' ? 'soc-ai-action-btn--primary' : ''}`}
                        onClick={() => runAction(action)}
                        disabled={thinking}
                      >
                        {action.type === 'prompt' && action.style === 'card' ? (
                          <>
                            <span className="soc-ai-action-card-body">
                              <span className="soc-ai-action-card-label">{action.label}</span>
                              {'description' in action && action.description ? (
                                <span className="soc-ai-action-card-desc">{action.description}</span>
                              ) : null}
                            </span>
                            <RightOutlined className="soc-ai-action-card-chevron" />
                          </>
                        ) : action.type === 'prompt' && (action.style === 'primary' || action.style === 'chip' || !action.style) ? (
                          action.label
                        ) : (
                          <>
                            {action.type === 'mailto' && <CustomerServiceOutlined />}
                            {action.type === 'checkout' && <ThunderboltOutlined />}
                            {action.type === 'navigate' && <CarryOutOutlined />}
                            {action.type === 'link' && <FileSearchOutlined />}
                            {'label' in action ? action.label : 'Open'}
                          </>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {msg.role === 'assistant' && msg.suggestions && conversationStarted && msg.suggestions.length > 0 ? (
                  <div className="soc-ai-suggestions soc-ai-suggestions--inline" aria-label="Suggestions">
                    {msg.suggestions.map((label) => (
                      <button
                        key={label}
                        type="button"
                        className="soc-ai-suggestion-chip"
                        onClick={() => runSuggestion(label)}
                        disabled={thinking}
                      >
                        {cleanChipLabel(label)}
                      </button>
                    ))}
                  </div>
                ) : null}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="soc-ai-msg-row soc-ai-msg-row--assistant">
                <AssistantAvatar size={14} pulse />
                <div className="soc-ai-thinking">
                  <Spin size="small" />
                  <span>Thinking…</span>
                  <span className="soc-ai-thinking-step soc-ai-thinking-step--active">
                    {THINKING_STEPS[thinkingStep]}
                  </span>
                </div>
              </div>
            )}
            {listening && (
              <div className="soc-ai-msg-row soc-ai-msg-row--assistant">
                <AssistantAvatar size={18} pulse />
                <div className="soc-ai-bubble soc-ai-bubble--assistant soc-ai-listening">
                  <AudioOutlined />
                  <div>
                    <span>Listening…</span>
                    <div className="soc-ai-listening-wave" aria-hidden>~~~~~~~~~~~~~~</div>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{selectedLanguage.voiceHint}</span>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </div>

          <footer className="soc-ai-footer">
            <div className="soc-ai-composer-toolbar">
              <div className="soc-ai-composer-tools">
                <button
                  type="button"
                  className="soc-ai-tool-btn"
                  onClick={() => void sendMessage('diagnose with photo')}
                  disabled={thinking}
                  aria-label="Attach photo"
                >
                  <PaperClipOutlined />
                </button>
                <Select
                  value={languageCode}
                  onChange={(value) => {
                    stopListening();
                    setLanguageCode(value);
                  }}
                  options={INDIAN_LANGUAGES.map((lang) => ({ value: lang.code, label: lang.label }))}
                  className="soc-ai-header-lang"
                  suffixIcon={<TranslationOutlined />}
                  aria-label="Choose language"
                  popupMatchSelectWidth={false}
                />
              </div>
            </div>
            <div className="soc-ai-composer-box">
              <Input.TextArea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    void sendMessage(draft);
                  }
                }}
                placeholder={selectedLanguage.placeholder}
                autoSize={{ minRows: 2, maxRows: 4 }}
                disabled={thinking}
                className="soc-ai-input"
              />
              <div className="soc-ai-composer-actions">
                <button
                  type="button"
                  className="soc-ai-think-btn"
                  onClick={() => inputRef.current?.focus()}
                  disabled={thinking}
                >
                  <BulbOutlined />
                  Think
                </button>
                <div className="soc-ai-composer-right">
                  <button
                    type="button"
                    className={`soc-ai-mic-btn ${listening ? 'soc-ai-mic-btn--listening' : ''}`}
                    onClick={listening ? stopListening : startListening}
                    disabled={thinking || !speechSupported}
                    aria-label={listening ? 'Stop listening' : 'Speak'}
                  >
                    <AudioOutlined />
                  </button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => void sendMessage(draft)}
                    disabled={!draft.trim() || thinking}
                    className="soc-ai-send"
                    aria-label="Send message"
                  />
                </div>
              </div>
            </div>
          </footer>
        </div>
        </>
      )}
    </>
  );
};

export default SupportChatWidget;
